import { Box, Grid2, Skeleton, styled, Typography } from "@mui/material";
import PurpleBlinkingLottie from "../../Components/Lotties/PurpleBlinkingLottie.json";
import Lottie from "react-lottie";
import { StyledCard } from "./components/StyledComponents";
import CustomIcon from "../../Components/CustomIcon";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import { getInventoryFilterUrl } from "../Overview/CommonFunctions";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BATTERY_STATUS_MAP, PING_STATUS_MAP, TAG_BATTERY_STATUS_MAP } from "../../Helper/DigitalTwin/DataFile";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";
import VehicleStatusInfoTooltip from "../../Components/VehicleStatusInfoTooltip";

const StatusCard = styled(Box)(({ item }) => ({
  backgroundColor: "#F9F9F9",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "16px",
  flex: 1,
  gap: "36px",
  cursor: item.filterUrl ? "pointer" : "default",
  ...(item.filterUrl && {
    "&:hover": {
      backgroundColor: "#F7F7FF",
    },
    "&:hover .chevron-icon": {
      color: "#4D44D8",
    },
  }),
}));

/**
 * Custom platform LiveVehicleStatusCard.
 * Inherited from the Coulomb version — shows Charging / Discharging / Idle / Offline counts.
 */
const LiveVehicleStatusCard = ({ stats, globalFilters, loading }) => {
  const { baseEntityTypeLable } = useBaseEntityInfo();
  const {
    Charging: chargingCount,
    Discharging: dischargingCount,
    Idle: idleCount,
    Offline: offlineCount,
  } = stats;

  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: PurpleBlinkingLottie,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
  };

  const [cards, setCards] = useState([]);

  useEffect(() => {
    const CUSTOM_INVENTORY = '/custom-observability/inventory';
    const cardConfigs = [
      {
        label: "CHARGING",
        value: chargingCount ?? "-",
        icon: <CustomIcon name="charge-icon" sx={{ color: "#008A15", width: 18, height: 18 }} />,
        filterUrl:
          typeof chargingCount === "number"
            ? getInventoryFilterUrl(globalFilters, { batteryStatus: [BATTERY_STATUS_MAP.CHARGING], pingStatus: [PING_STATUS_MAP.ACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] }, {}, CUSTOM_INVENTORY)
            : undefined,
      },
      {
        label: "DISCHARGING",
        value: dischargingCount ?? "-",
        icon: <CustomIcon name="discharge-icon" sx={{ color: "#DCA101", width: 18, height: 18 }} />,
        filterUrl:
          typeof dischargingCount === "number"
            ? getInventoryFilterUrl(globalFilters, { batteryStatus: [BATTERY_STATUS_MAP.DISCHARGING], pingStatus: [PING_STATUS_MAP.ACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] }, {}, CUSTOM_INVENTORY)
            : undefined,
      },
      {
        label: "IDLE",
        value: idleCount ?? "-",
        icon: <CustomIcon name="pause-icon" sx={{ width: 18, height: 18 }} />,
        filterUrl:
          typeof idleCount === "number"
            ? getInventoryFilterUrl(globalFilters, { batteryStatus: [BATTERY_STATUS_MAP.IDLE], pingStatus: [PING_STATUS_MAP.ACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] }, {}, CUSTOM_INVENTORY)
            : undefined,
      },
      {
        label: "OFFLINE",
        value: offlineCount ?? "-",
        icon: <FiberManualRecordIcon sx={{ color: "#8F8F8F" }} />,
        filterUrl:
          typeof offlineCount === "number"
            ? getInventoryFilterUrl(globalFilters, { pingStatus: [PING_STATUS_MAP.INACTIVE], tag_battery_status: [TAG_BATTERY_STATUS_MAP.OPERATIONAL, TAG_BATTERY_STATUS_MAP.NON_OPERATIONAL] }, { sortBy: 'lastGPSPing', orderBy: 'asc' }, CUSTOM_INVENTORY)
            : undefined,
      },
    ];
    setCards(cardConfigs);
  }, [globalFilters, stats]);

  return (
    <StyledCard>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 1 }}>
        <Grid2 container gap={1}>
          <Box>
            <Lottie options={lottieOptions} height={20} width={20} />
          </Box>
          <Typography variant="fw500s16lh20c000000">
            Live {baseEntityTypeLable} Status
          </Typography>
          <VehicleStatusInfoTooltip />
        </Grid2>
        <Grid2 container gap={1}>
          {cards.map((item) => {
            const Wrapper = item.filterUrl ? Link : Box;
            return (
              <Wrapper
                key={item.label}
                {...(item.filterUrl
                  ? {
                      to: item.filterUrl,
                      target: "_blank",
                      style: { display: "contents" },
                    }
                  : { style: { display: "contents", pointerEvents: "none" } })}
              >
                <PrimaryTooltip title={item.filterUrl ? "View in Inventory" : ""}>
                  <StatusCard item={item}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {item.icon}
                      <Typography variant="fw500s14c494949" sx={{ lineHeight: "24px" }}>
                        {item.label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      {loading ? (
                        <Skeleton variant="text" width={48} height={36} sx={{ ml: 1 }} />
                      ) : (
                        <Typography variant="fw500s20c000000" sx={{ fontSize: "28px", lineHeight: "28px", ml: 1 }}>
                          {item.value}
                        </Typography>
                      )}
                      {item.filterUrl && (
                        <ChevronRightIcon className="chevron-icon" fontSize="large" sx={{ color: "#8F8F8F" }} />
                      )}
                    </Box>
                  </StatusCard>
                </PrimaryTooltip>
              </Wrapper>
            );
          })}
        </Grid2>
      </Box>
    </StyledCard>
  );
};

export default LiveVehicleStatusCard;
